<template>
  <ValidationObserver
    ref="formValidation"
    v-slot="{ pristine, handleSubmit, invalid }"
    slim
  >
    <form
      role="form"
      class="user-group form-horizontal"
      @submit.prevent="handleSubmit(onSaveAll)"
    >
      <fieldset>
        <Field
          :label="$t('LABEL_NAME')"
          :has-tooltip="true"
          :tooltip-text="$t('TOOLTIP_NAME')"
        >
          <template #element>
            <ValidationProvider
              rules="required"
              slim
            >
              <div class="col-sm-5 col-md-3">
                <input
                  v-model="groupName"
                  type="text"
                  class="form-control"
                  required="true"
                >
              </div>
            </ValidationProvider>
          </template>
        </Field>
      
        <Field
          :label="$t('LABEL_TYPE')"
          :has-tooltip="true"
          :tooltip-text="$t('TOOLTIP_TYPE')"
        >
          <template #element>
            <ValidationProvider
              rules="required"
              slim
            >
              <div class="col-sm-5 col-md-3">
                <v-select
                  v-if="newGroup"
                  v-model="type"
                  class="form-input"
                  :options="[{label: 'Publisher', id: 'Affiliate'}, {label: 'Buyer', id: 'Buyer'}]"
                  :searchable="false"
                  :clearable="false"
                  :reduce="option => option.id"
                  @option:selecting="onTypeChange"
                />
                <input
                  v-if="!newGroup"
                  v-tooltip.right="$t('TOOLTIP_NEW_TYPE')"
                  type="text"
                  class="form-control"
                  disabled="true"
                  :value="typeReadOnly"
                >
              </div>
            </ValidationProvider>
          </template>
        </Field>
        
        <Field
          :label="$t('LABEL_ADMIN_REPORT')"
          :has-tooltip="true"
          :tooltip-text="$t('TOOLTIP_ADMIN_REPORT')"
        >
          <template #element>
            <ValidationProvider
              rules="required"
              slim
            >
              <div class="col-sm-5 col-md-3">
                <nzToggle v-model="adminReportView" />
              </div>
            </ValidationProvider>
          </template>
        </Field>
        
        <Field
          :label="$t('LABEL_READ_ONLY')"
          :has-tooltip="true"
          :tooltip-text="$t('TOOLTIP_READ_ONLY')"
        >
          <template #element>
            <ValidationProvider
              rules="required"
              slim
            >
              <div class="col-sm-5 col-md-3">
                <nzToggle v-model="readOnly" />
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <Field
          :label="$t('LABEL_LINKED_ITEMS')"
          :has-tooltip="true"
          :tooltip-text="$t('TOOLTIP_LINKED_ITEMS')"
        >
          <template #element>
            <div class="col-sm-9 col-md-6">
              <ValidationProvider 
                ref="itemIdsValidation"
                slim
              >
                <MultiSelect
                  v-model="selectedItemIds"
                  class="multi-select-lg"
                  :list-items="items"
                  display-property="name"
                  value-property="id"
                  :no-options-text="$t('EMPTY_LINKED_ITEMS')"
                  @input="onItemIdsChange"
                />
              </ValidationProvider>
            </div>
          </template>
        </Field>

        <div class="form-group">
          <div class="col-sm-8 col-md-8 col-md-offset-3 col-sm-offset-3">            
            <PrimaryButton
              size="sm"
              :disabled="pristine || invalid"
              :show-loader-on-form="showLoader"
              @click="submit"
            >
              {{ $t('SAVE') }}
            </PrimaryButton>

            <button
              v-t="'CANCEL'"
              class="btn btn-sm btn-danger m-l-15"
              @click="cancel"
            />
          </div>
        </div>
      </fieldset>
    </form>
  </ValidationObserver>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import messages from '../locales';
import sharedMessages from '@/core/locales';

import Field from '@/core/components/LabelAndElement/LabelAndElement';
import MultiSelect from '@/core/components/MultiSelect/MultiSelect';
import nzToggle from '@/core/components/nzToggle/nzToggle';
import PrimaryButton from '@/core/components/PrimaryButton';

export default {
  name: 'UserGroupForm',
  components: { Field, MultiSelect, nzToggle, PrimaryButton },
  props:{ 
    newGroup: {
      type: Boolean,
      default: false
    },
    group: {
      type: Object,
      default: () => null
    },
    showLoader: {
      type: Boolean,
      default: () => true
    }
  },
  data() {
    return {
      groupName: this.group?.name ?? '',
      type: this.group?.type ?? 'Affiliate',
      readOnly: this.group?.readOnly ?? false,
      adminReportView: this.group?.adminReportView ?? false,
      linkedPublisherIds: this.group?.type == 'Affiliate' ? this.group?.linkedItemIds : [],
      linkedBuyerIds: this.group?.type == 'Buyer' ? this.group?.linkedItemIds : [],
      selectedItemIds: this.group?.linkedItemIds ?? []
    };
  },
  computed:{
    ...mapState([
      'affiliates',
      'buyers'
    ]),
    items() {
      if (this.type === 'Affiliate') {
        return this.affiliates;
      }

      if (this.type === 'Buyer') {
        return this.buyers;
      }

      return [];
    },
    typeReadOnly() {
      if (this.type === 'Affiliate') {
        return 'Publisher';
      }

      return this.type;
    }
  },
  created() {
    this.getBuyers();
    this.getAffiliates();
  },
  methods:{
    ...mapActions([
      'getBuyers',
      'getAffiliates'
    ]),
    submit() {
      this.$emit('submit', {
        id: this.group?.id ?? undefined,
        name: this.groupName,
        type: this.type,
        readOnly: this.readOnly,
        adminReportView: this.adminReportView,
        linkedItemIds: this.selectedItemIds
      });
    },
    cancel() {
      this.$emit('cancel');
    },
    onTypeChange(newType) {
      if (this.type === 'Affiliate') {
        this.linkedPublisherIds = this.selectedItemIds;
      }

      if (this.type === 'Buyer') {
        this.linkedBuyerIds = this.selectedItemIds;
      }

      this.selectedItemIds = [];

      if (newType?.id === 'Affiliate') {
        this.selectedItemIds = this.linkedPublisherIds;
      }

      if (newType?.id === 'Buyer') {
        this.selectedItemIds = this.linkedBuyerIds;
      }
    },
    onItemIdsChange() {
      this.$refs.itemIdsValidation.setFlags({
        dirty: true,
        pristine: false
      });
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  },
};
</script>

<style lang="scss">
form.user-group {
  .multi-select-lg {
    width: 100%;
  }

  .v-select .vs__selected {
    height: 32px;
    padding: 8px 8px 7px 10px;
    border-bottom: 1px solid transparent;
  }
}
</style>